import { useEffect, useState } from "react";
import { Lock, Loader2, Phone, Mail, ImageIcon, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, value }) =>
  value ? (
    <p className="text-sm">
      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</span>
      <span className="mt-0.5 block font-semibold text-navy">{value}</span>
    </p>
  ) : null;

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("teos_admin_token") || "");
  const [input, setInput] = useState("");
  const [subs, setSubs] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = async (t) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/admin/submissions?token=${encodeURIComponent(t)}`);
      if (!res.ok) throw new Error("unauthorized");
      const data = await res.json();
      setSubs(data.submissions);
      localStorage.setItem("teos_admin_token", t);
      setToken(t);
    } catch {
      setError("Invalid access code. Please try again.");
      setSubs(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-cream py-14">
      <div className="mx-auto max-w-4xl px-5">
        <div className="flex items-center justify-between">
          <Link to="/" data-testid="admin-back-link" className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-gold-muted">
            <ArrowLeft size={16} /> Back to website
          </Link>
          <span className="rounded-xl bg-white p-2 shadow-sm">
            <img src="/assets/logo.png" alt="Teo's Painting LLC" className="h-8 w-auto" />
          </span>
        </div>

        <h1 className="mt-10 font-display text-3xl font-bold text-navy sm:text-4xl">Estimate Requests</h1>
        <p className="mt-2 text-sm text-slate-500">Every form submission from your website lands here (and in your email inbox).</p>

        {!subs && (
          <form
            data-testid="admin-login-form"
            onSubmit={(e) => {
              e.preventDefault();
              load(input);
            }}
            className="mx-auto mt-12 max-w-sm rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-lg"
          >
            <Lock size={26} className="mx-auto text-gold" />
            <p className="mt-3 text-sm font-bold text-navy">Owner access</p>
            <input
              data-testid="admin-token-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Access code"
              className="mt-4 w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm outline-none focus:border-gold focus:ring-2 focus:ring-gold/30"
            />
            {error && <p data-testid="admin-login-error" className="mt-2 text-xs font-semibold text-red-600">{error}</p>}
            <button
              data-testid="admin-login-button"
              disabled={loading}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-gold py-3 text-[12px] font-extrabold tracking-[0.14em] text-navy hover:bg-gold-light disabled:opacity-60"
            >
              {loading && <Loader2 size={15} className="animate-spin" />} VIEW REQUESTS
            </button>
          </form>
        )}

        {subs && (
          <div className="mt-10 space-y-6" data-testid="admin-submissions-list">
            {subs.length === 0 && (
              <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
                No estimate requests yet. Share your website and they'll appear here.
              </p>
            )}
            {subs.map((s, i) => (
              <article key={s.id} data-testid={`admin-submission-${i}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h2 className="font-display text-xl font-bold text-navy">{s.name}</h2>
                    <p className="mt-1 font-mono text-[10px] tracking-[0.2em] text-slate-400">
                      {new Date(s.created_at).toLocaleString()} · {s.city}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <a href={`tel:+1${(s.phone || "").replace(/\D/g, "")}`} data-testid={`admin-submission-call-${i}`} className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-xs font-bold text-gold">
                      <Phone size={13} /> Call
                    </a>
                    <a href={`mailto:${s.email}`} data-testid={`admin-submission-email-${i}`} className="flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-navy hover:border-gold">
                      <Mail size={13} /> Email
                    </a>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-3">
                  <Field label="Phone" value={s.phone} />
                  <Field label="Email" value={s.email} />
                  <Field label="City" value={s.city} />
                  <Field label="Project Type" value={s.project_type} />
                  <Field label="Service" value={s.service} />
                  <Field label="Interior / Exterior" value={s.scope} />
                  <Field label="Approx. Size" value={s.size} />
                </div>

                {s.message && (
                  <p className="mt-5 rounded-xl bg-slate-50 p-4 text-sm leading-relaxed text-slate-600">{s.message}</p>
                )}

                {s.photos?.length > 0 && (
                  <div className="mt-5">
                    <p className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      <ImageIcon size={13} /> {s.photos.length} photo(s)
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      {s.photos.map((f) => (
                        <a
                          key={f}
                          href={`${API}/admin/submissions/${s.id}/photos/${f}?token=${encodeURIComponent(token)}`}
                          target="_blank"
                          rel="noreferrer"
                          data-testid={`admin-photo-${s.id}-${f}`}
                        >
                          <img
                            src={`${API}/admin/submissions/${s.id}/photos/${f}?token=${encodeURIComponent(token)}`}
                            alt="Customer project upload"
                            className="h-20 w-20 rounded-lg border border-slate-200 object-cover transition-transform hover:scale-105"
                          />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
